import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { dashboardService } from '../services/dashboardService';
import { LineChart } from '../components/charts/LineChart';
import { MetricCard } from '../components/dashboard/MetricCard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`performance-tabpanel-${index}`}
      aria-labelledby={`performance-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `performance-tab-${index}`,
    'aria-controls': `performance-tabpanel-${index}`,
  };
}

const ModelPerformance: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [timeRange, setTimeRange] = useState('7d');
  const [marketType, setMarketType] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [performanceData, setPerformanceData] = useState<any>({
    overall_accuracy: 76.8,
    by_market: {},
    by_league: {},
    trend: []
  });
  const [recentPredictions, setRecentPredictions] = useState<any[]>([]);
  const theme = useTheme();
  
  useEffect(() => {
    fetchPerformanceData();
    
    // Set up polling for performance data (less frequent than system health)
    const interval = setInterval(() => {
      fetchPerformanceData();
    }, 300000); // Refresh every 5 minutes
    
    return () => clearInterval(interval);
  }, [timeRange, marketType]);
  
  const fetchPerformanceData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch performance data based on selected filters
      const data = await dashboardService.getModelPerformance(marketType, timeRange);
      setPerformanceData(data);
      
      // Fetch recent predictions
      const predictionsData = await dashboardService.getLivePredictions(20);
      setRecentPredictions(predictionsData.predictions);
      
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to fetch performance data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleTimeRangeChange = (event: any) => {
    setTimeRange(event.target.value);
  };
  
  const handleMarketTypeChange = (event: any) => {
    setMarketType(event.target.value);
  };
  
  // Prepare chart data for accuracy trend
  const accuracyTrendData = {
    labels: performanceData.trend ? performanceData.trend.map((item: any) => {
      const date = new Date(item.date);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    }) : [],
    datasets: [
      {
        label: 'Prediction Accuracy',
        data: performanceData.trend ? performanceData.trend.map((item: any) => item.accuracy) : [],
        borderColor: theme.palette.primary.main,
        fill: true,
      }
    ]
  };
  
  // Mock data for market performance
  const marketPerformanceData = {
    labels: ['1X2', 'Over/Under', 'BTTS', 'Correct Score', 'Asian Handicap'],
    datasets: [
      {
        label: 'Accuracy by Market',
        data: [78.2, 75.5, 72.3, 65.8, 73.1],
        backgroundColor: [
          theme.palette.primary.main,
          theme.palette.secondary.main,
          theme.palette.success.main,
          theme.palette.warning.main,
          theme.palette.info.main,
        ],
        borderColor: theme.palette.background.paper,
        borderWidth: 2,
      }
    ]
  };
  
  // Mock data for league performance
  const leaguePerformanceData = {
    labels: ['Premier League', 'La Liga', 'Bundesliga', 'Serie A', 'Ligue 1'],
    datasets: [
      {
        label: 'Accuracy by League',
        data: [82.4, 79.6, 78.1, 75.3, 77.8],
        backgroundColor: [
          theme.palette.primary.main,
          theme.palette.secondary.main,
          theme.palette.success.main,
          theme.palette.warning.main,
          theme.palette.info.main,
        ],
        borderColor: theme.palette.background.paper,
        borderWidth: 2,
      }
    ]
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h5" component="h1">
          Model Performance Monitoring
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120, mr: 2 }}>
            <InputLabel id="time-range-label">Time Range</InputLabel>
            <Select
              labelId="time-range-label"
              id="time-range"
              value={timeRange}
              onChange={handleTimeRangeChange}
              label="Time Range"
            >
              <MenuItem value="1d">Last 24 Hours</MenuItem>
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="90d">Last 90 Days</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" sx={{ minWidth: 150, mr: 2 }}>
            <InputLabel id="market-type-label">Market Type</InputLabel>
            <Select
              labelId="market-type-label"
              id="market-type"
              value={marketType}
              onChange={handleMarketTypeChange}
              label="Market Type"
            >
              <MenuItem value="all">All Markets</MenuItem>
              <MenuItem value="1x2">Match Result (1X2)</MenuItem>
              <MenuItem value="over_under">Over/Under</MenuItem>
              <MenuItem value="btts">Both Teams to Score</MenuItem>
              <MenuItem value="correct_score">Correct Score</MenuItem>
              <MenuItem value="asian_handicap">Asian Handicap</MenuItem>
            </Select>
          </FormControl>
          
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
          
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={fetchPerformanceData}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* Overall Performance Metrics */}
        <Grid item xs={12} md={3}>
          <MetricCard 
            title="Overall Accuracy"
            value={performanceData.overall_accuracy}
            unit="%"
            status={
              performanceData.overall_accuracy > 80 
                ? 'success' 
                : performanceData.overall_accuracy > 70 
                  ? 'warning' 
                  : 'error'
            }
            icon="accuracy"
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <MetricCard 
            title="Match Result (1X2)"
            value={performanceData.by_market['1x2'] || 78.2}
            unit="%"
            status={
              (performanceData.by_market['1x2'] || 78.2) > 80 
                ? 'success' 
                : (performanceData.by_market['1x2'] || 78.2) > 70 
                  ? 'warning' 
                  : 'error'
            }
            icon="custom"
            customIcon={<RefreshIcon fontSize="large" />}
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <MetricCard 
            title="Over/Under"
            value={performanceData.by_market['over_under'] || 75.5}
            unit="%"
            status={
              (performanceData.by_market['over_under'] || 75.5) > 80 
                ? 'success' 
                : (performanceData.by_market['over_under'] || 75.5) > 70 
                  ? 'warning' 
                  : 'error'
            }
            icon="custom"
            customIcon={<RefreshIcon fontSize="large" />}
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <MetricCard 
            title="Both Teams to Score"
            value={performanceData.by_market['btts'] || 72.3}
            unit="%"
            status={
              (performanceData.by_market['btts'] || 72.3) > 80 
                ? 'success' 
                : (performanceData.by_market['btts'] || 72.3) > 70 
                  ? 'warning' 
                  : 'error'
            }
            icon="custom"
            customIcon={<RefreshIcon fontSize="large" />}
          />
        </Grid>
        
        {/* Accuracy Trend Chart */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Prediction Accuracy Trend</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 350 }}>
              <LineChart 
                labels={accuracyTrendData.labels}
                datasets={accuracyTrendData.datasets}
                height={350}
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* Performance Tabs */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                aria-label="performance tabs"
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab label="Market Performance" {...a11yProps(0)} />
                <Tab label="League Performance" {...a11yProps(1)} />
                <Tab label="Recent Predictions" {...a11yProps(2)} />
              </Tabs>
            </Box>
            
            {/* Market Performance Tab */}
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                {Object.entries(performanceData.by_market || {}).map(([market, accuracy]: [string, any], index) => (
                  <Grid item xs={12} sm={6} md={4} key={market}>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        borderRadius: 2,
                        boxShadow: 1
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        {market.replace('_', ' ').toUpperCase()}
                      </Typography>
                      <Box 
                        sx={{ 
                          position: 'relative', 
                          display: 'inline-flex',
                          mb: 1
                        }}
                      >
                        <CircularProgress
                          variant="determinate"
                          value={accuracy}
                          size={120}
                          thickness={8}
                          sx={{
                            color: accuracy > 80 
                              ? theme.palette.success.main 
                              : accuracy > 70 
                                ? theme.palette.warning.main 
                                : theme.palette.error.main,
                          }}
                        />
                        <Box
                          sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="h4"
                            component="div"
                            color="text.primary"
                          >
                            {accuracy}%
                          </Typography>
                        </Box>
                      </Box>
                      <Chip 
                        label={
                          accuracy > 80 
                            ? "Excellent" 
                            : accuracy > 70 
                              ? "Good" 
                              : "Needs Improvement"
                        }
                        color={
                          accuracy > 80 
                            ? "success" 
                            : accuracy > 70 
                              ? "warning" 
                              : "error"
                        }
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </TabPanel>
            
            {/* League Performance Tab */}
            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={3}>
                {Object.entries(performanceData.by_league || {}).map(([league, accuracy]: [string, any], index) => (
                  <Grid item xs={12} sm={6} md={4} key={league}>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        borderRadius: 2,
                        boxShadow: 1
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        {league.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                      </Typography>
                      <Box 
                        sx={{ 
                          position: 'relative', 
                          display: 'inline-flex',
                          mb: 1
                        }}
                      >
                        <CircularProgress
                          variant="determinate"
                          value={accuracy}
                          size={120}
                          thickness={8}
                          sx={{
                            color: accuracy > 80 
                              ? theme.palette.success.main 
                              : accuracy > 70 
                                ? theme.palette.warning.main 
                                : theme.palette.error.main,
                          }}
                        />
                        <Box
                          sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="h4"
                            component="div"
                            color="text.primary"
                          >
                            {accuracy}%
                          </Typography>
                        </Box>
                      </Box>
                      <Chip 
                        label={
                          accuracy > 80 
                            ? "Excellent" 
                            : accuracy > 70 
                              ? "Good" 
                              : "Needs Improvement"
                        }
                        color={
                          accuracy > 80 
                            ? "success" 
                            : accuracy > 70 
                              ? "warning" 
                              : "error"
                        }
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </TabPanel>
            
            {/* Recent Predictions Tab */}
            <TabPanel value={tabValue} index={2}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Match</TableCell>
                      <TableCell>League</TableCell>
                      <TableCell>Prediction</TableCell>
                      <TableCell>Confidence</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentPredictions.map((prediction) => (
                      <TableRow key={prediction.id}>
                        <TableCell>{prediction.match}</TableCell>
                        <TableCell>{prediction.league}</TableCell>
                        <TableCell>{prediction.prediction}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={prediction.confidence} 
                                color={
                                  prediction.confidence > 80 
                                    ? 'success' 
                                    : prediction.confidence > 60 
                                      ? 'warning' 
                                      : 'error'
                                }
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </Box>
                            <Box sx={{ minWidth: 35 }}>
                              <Typography variant="body2" color="text.secondary">
                                {`${prediction.confidence.toFixed(1)}%`}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={prediction.value ? "Value Bet" : "No Value"} 
                            color={prediction.value ? "success" : "default"} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(prediction.kickoff).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ModelPerformance;
